import {HorizontalTextAlign, TextWidgetData, VerticalTextAlign, Widget} from '@dv5/domain'
import React, {FC, useMemo} from 'react'
import {ColorPicker} from '../common/ColorPicker'
import {TextAlignmentPicker} from '../common/TextAlignmentPicker'
import {TextFormatPicker} from '../text-widget/TextFormatPicker'
import {COLOR_PRESETS} from '../sticky-note/constants'
import {WidgetDataChange, canvasWidgetsDataService} from '../../services/canvasWidgetsDataService'

interface Props {
    canvasId: string
    widgets: Widget[]
}

interface FormatProperties {
    bold?: boolean
    italic?: boolean
    underline?: boolean
    lineThrough?: boolean
}

export const TextWidgetPropertiesPanel: FC<Props> = ({
    canvasId,
    widgets,
}) => {
    const dataOf = (w: Widget) => w.data as TextWidgetData

    const bgColorMixed = useMemo(() => new Set(widgets.map(w => dataOf(w).bgColor)).size > 1, [widgets])
    const fgColorMixed = useMemo(() => new Set(widgets.map(w => dataOf(w).fgColor)).size > 1, [widgets])
    const horizontalAlignMixed = useMemo(
        () => new Set(widgets.map(w => dataOf(w).textProperties.horizontalTextAlign)).size > 1,
        [widgets],
    )
    const verticalAlignMixed = useMemo(
        () => new Set(widgets.map(w => dataOf(w).textProperties.verticalTextAlign)).size > 1,
        [widgets],
    )
    const boldMixed = useMemo(() => new Set(widgets.map(w => dataOf(w).textProperties.bold)).size > 1, [widgets])
    const italicMixed = useMemo(() => new Set(widgets.map(w => dataOf(w).textProperties.italic)).size > 1, [widgets])
    const underlineMixed = useMemo(
        () => new Set(widgets.map(w => dataOf(w).textProperties.underline)).size > 1,
        [widgets],
    )
    const lineThroughMixed = useMemo(
        () => new Set(widgets.map(w => dataOf(w).textProperties.lineThrough)).size > 1,
        [widgets],
    )
    const fontSizeMixed = useMemo(
        () => new Set(widgets.map(w => dataOf(w).textProperties.fontSize)).size > 1,
        [widgets],
    )

    const first = widgets.length > 0 ? dataOf(widgets[0]) : null

    const bgColor = !bgColorMixed && first ? first.bgColor : ''
    const fgColor = !fgColorMixed && first ? (first.fgColor || '#333333') : ''
    const horizontalAlign = !horizontalAlignMixed && first
        ? first.textProperties.horizontalTextAlign
        : null
    const verticalAlign = !verticalAlignMixed && first
        ? first.textProperties.verticalTextAlign
        : null
    const bold = !boldMixed && first ? first.textProperties.bold : null
    const italic = !italicMixed && first ? first.textProperties.italic : null
    const underline = !underlineMixed && first ? first.textProperties.underline : null
    const lineThrough = !lineThroughMixed && first ? first.textProperties.lineThrough : null
    const fontSize = !fontSizeMixed && first ? String(first.textProperties.fontSize ?? 14) : ''

    const applyPatch = (
        patchedPaths: Array<string>,
        makeData: (data: TextWidgetData) => TextWidgetData,
    ) => {
        const changes: Array<WidgetDataChange> = widgets.map(widget => ({
            widgetId: widget.id,
            patchedPaths,
            data: makeData(dataOf(widget)),
        }))
        canvasWidgetsDataService.changeWidgetsData(canvasId, changes)
    }

    const handleBgColorChange = (color: string) => {
        applyPatch(['bgColor'], data => ({...data, bgColor: color}))
    }

    const handleFgColorChange = (color: string) => {
        applyPatch(['fgColor'], data => ({...data, fgColor: color}))
    }

    const handleHorizontalAlignChange = (horizontalTextAlign: HorizontalTextAlign) => {
        applyPatch(['textProperties.horizontalTextAlign'], data => ({
            ...data,
            textProperties: {...data.textProperties, horizontalTextAlign},
        }))
    }

    const handleVerticalAlignChange = (verticalTextAlign: VerticalTextAlign) => {
        applyPatch(['textProperties.verticalTextAlign'], data => ({
            ...data,
            textProperties: {...data.textProperties, verticalTextAlign},
        }))
    }

    const handleFormatChange = (properties: FormatProperties) => {
        const patchedPaths = Object.keys(properties).map(prop => `textProperties.${prop}`)
        applyPatch(patchedPaths, data => ({
            ...data,
            textProperties: {...data.textProperties, ...properties},
        }))
    }

    const handleFontSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const parsed = parseInt(e.target.value, 10)
        if (isNaN(parsed) || parsed < 1) return
        applyPatch(['textProperties.fontSize'], data => ({
            ...data,
            textProperties: {...data.textProperties, fontSize: parsed},
        }))
    }

    return (
        <div
            className='align-widgets-bar__properties'
            onMouseDown={e => e.stopPropagation()}
        >
            <ColorPicker
                value={bgColor}
                presets={COLOR_PRESETS}
                onChange={handleBgColorChange}
                mixed={bgColorMixed}
            />
            <ColorPicker
                value={fgColor}
                presets={COLOR_PRESETS}
                onChange={handleFgColorChange}
                mixed={fgColorMixed}
            />
            <TextAlignmentPicker
                horizontalAlign={horizontalAlign}
                verticalAlign={verticalAlign}
                onHorizontalAlignChange={handleHorizontalAlignChange}
                onVerticalAlignChange={handleVerticalAlignChange}
            />
            <TextFormatPicker
                prefix="align-widgets-bar"
                bold={bold}
                italic={italic}
                underline={underline}
                lineThrough={lineThrough}
                onChange={handleFormatChange}
            />
            <input
                className='align-widgets-bar__font-size-input'
                type='number'
                min={8}
                max={200}
                value={fontSize}
                onChange={handleFontSizeChange}
                placeholder='14'
                aria-label='Font size'
            />
        </div>
    )
}
